import {
  useAuthContext
} from "./chunk-6HLSZPFF.js";
import {
  AnalyticsEvents,
  useAnalyticsSafe
} from "./chunk-MXQ2GIJ4.js";
import {
  logger
} from "./chunk-4R6JFI7D.js";

// src/cart/types/cart-errors.ts
var CartError = class extends Error {
  constructor(message, code, details) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = "CartError";
  }
};

// src/cart/types/cart-config.ts
var DEFAULT_CART_CONFIG = {
  maxItems: 100,
  maxQuantityPerItem: 999,
  expirationMs: 7 * 24 * 60 * 60 * 1e3,
  // 7 days
  enableAutoValidation: true,
  storageDebounceMs: 500
};

// src/cart/cart-context.ts
import { createContext } from "react";
var CartContext = createContext(null);

// src/cart/cart-hooks.ts
import { useContext, useMemo } from "react";
function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
function useCartDrawer() {
  const { isOpen, toggleCart, setCartOpen } = useCart();
  return useMemo(() => ({
    isOpen,
    open: () => setCartOpen(true),
    close: () => setCartOpen(false),
    toggle: toggleCart
  }), [isOpen, setCartOpen, toggleCart]);
}
function useProductInCart(productId) {
  const { items, addItem, removeItem, updateQuantity } = useCart();
  return useMemo(() => {
    const cartItem = items.find((item) => item.productId === productId);
    const isInCart = !!cartItem;
    const quantity = cartItem?.quantity ?? 0;
    return {
      isInCart,
      quantity,
      cartItem,
      addToCart: (input) => addItem(input),
      removeFromCart: () => cartItem && removeItem(cartItem.id),
      updateQuantity: (newQuantity) => cartItem && updateQuantity(cartItem.id, newQuantity)
    };
  }, [items, productId, addItem, removeItem, updateQuantity]);
}
function useCartTotals() {
  const { totalPrice, totalItems } = useCart();
  return useMemo(() => ({
    totalPrice,
    totalItems,
    formattedTotal: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(totalPrice),
    hasItems: totalItems > 0
  }), [totalPrice, totalItems]);
}
function useCartLoading() {
  const { isLoading, loadingStates } = useCart();
  return useMemo(() => ({
    isLoading,
    isAddingItem: loadingStates.isAddingItem,
    isClearingCart: loadingStates.isClearingCart,
    isRemovingItem: (itemId) => loadingStates.itemOperations[itemId]?.isRemoving ?? false,
    isUpdatingItem: (itemId) => loadingStates.itemOperations[itemId]?.isUpdating ?? false,
    isAnyOperationLoading: loadingStates.isAddingItem || loadingStates.isClearingCart || Object.values(loadingStates.itemOperations).some((op) => op.isRemoving || op.isUpdating)
  }), [isLoading, loadingStates]);
}
function useCartAuth() {
  const { isAuthenticated, authLoading, securityContext } = useCart();
  return useMemo(() => ({
    isAuthenticated,
    isLoading: authLoading,
    securityContext,
    canUseCart: isAuthenticated && !authLoading && !!securityContext,
    requiresAuth: !isAuthenticated
  }), [isAuthenticated, authLoading, securityContext]);
}

// src/cart/cart-utils.ts
import {
  buildCandidatesFromProductPricingDto,
  selectPrice,
  lowestWinsStrategy
} from "@flote/pricing";
import { B2BWaveProductStatuses } from "@flote/constants";
function buildCandidatesFromDto(standardPrice, customerPrice, buyerPrice, buyerPriceTiers, discountTiers) {
  return buildCandidatesFromProductPricingDto({
    standardPrice,
    customerPrice,
    buyerPrice,
    buyerPriceTiers,
    discountTiers
  });
}
function generateCartItemId() {
  return `cart_${crypto.randomUUID()}`;
}
function generateInstanceId() {
  const urlHash = window.location.origin;
  const userAgentHash = navigator.userAgent.substring(0, 50);
  const combined = urlHash + userAgentHash;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const hashStr = Math.abs(hash).toString(16).padStart(8, "0");
  return `flote_${hashStr}`;
}
function calculateCartTotals(items) {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => {
    const candidates = buildCandidatesFromDto(item.price, item.customerPrice, item.buyerPrice, item.buyerPriceTiers, item.discountTiers);
    const result = selectPrice(candidates, item.quantity, lowestWinsStrategy);
    const effectivePrice = result?.winner.amount ?? item.price;
    return sum + effectivePrice * item.quantity;
  }, 0);
  return { totalItems, totalPrice };
}
function createCartItem(product, quantity, securityContext, brandName) {
  const getPrice = () => {
    if (product.pricing?.price) {
      return parseFloat(product.pricing.price);
    }
    return product.priceMsrp || 0;
  };
  const getCustomerPrice = () => {
    if (product.customerPrice?.price) {
      return parseFloat(product.customerPrice.price);
    }
    return void 0;
  };
  const getBuyerPrice = () => {
    if (product.buyerPrice?.amount) {
      const parsed = parseFloat(product.buyerPrice.amount);
      return Number.isFinite(parsed) ? parsed : void 0;
    }
    return void 0;
  };
  const customerPrice = getCustomerPrice();
  const buyerPrice = getBuyerPrice();
  return {
    id: generateCartItemId(),
    productId: product.id.toString(),
    companyId: securityContext.companyId,
    addedBy: securityContext.userId,
    addedAt: (/* @__PURE__ */ new Date()).toISOString(),
    quantity,
    name: product.name,
    price: getPrice(),
    customerPrice,
    buyerPrice,
    buyerPriceTiers: product.buyerPriceTiers,
    hasCustomerPricing: !!customerPrice,
    discountTiers: product.discountTiers,
    imageUrl: product.imageUrl,
    brandId: product.brandId?.toString() || "",
    brandName,
    inventory: 1,
    packageQuantity: product.packageQuantity || 1,
    type: product.categoryPath || "",
    thcContent: "",
    weight: "",
    statusId: product.statusId ?? void 0,
    canBackorder: product.canBackorder ?? void 0,
    requiresMetrcTracking: product.requiresMetrcTracking ?? false,
    minOrderQuantity: product.minOrderQuantity ?? void 0,
    maxOrderQuantity: product.maxOrderQuantity ?? void 0,
    orderIncrement: product.orderIncrement ?? void 0
  };
}
function getEffectivePrice(item) {
  const candidates = buildCandidatesFromDto(item.price, item.customerPrice, item.buyerPrice, item.buyerPriceTiers, item.discountTiers);
  const result = selectPrice(candidates, item.quantity, lowestWinsStrategy);
  return result?.winner.amount ?? item.price;
}
function getPriceDisplayInfo(item) {
  const candidates = buildCandidatesFromDto(item.price, item.customerPrice, item.buyerPrice, item.buyerPriceTiers, item.discountTiers);
  const result = selectPrice(candidates, item.quantity, lowestWinsStrategy);
  const winner = result?.winner;
  const effectivePrice = winner?.amount ?? item.price;
  const priceType = winner ? winner.scope === "global-tier" ? "bulk" : winner.scope === "org" ? "buyer" : winner.scope : "standard";
  const hasDiscount = priceType !== "standard";
  const savings = hasDiscount ? item.price - effectivePrice : 0;
  const savingsPercentage = hasDiscount && item.price > 0 ? Math.round(savings / item.price * 100) : 0;
  return {
    effectivePrice,
    priceType,
    originalPrice: hasDiscount ? item.price : void 0,
    hasCustomerPricing: priceType === "customer",
    hasBulkPricing: priceType === "bulk",
    hasBuyerPricing: priceType === "buyer",
    savings: savings > 0 ? savings : void 0,
    savingsPercentage: savingsPercentage > 0 ? savingsPercentage : void 0
  };
}
function formatCurrency(amount, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(amount);
}
function isCartItemBackordered(item) {
  const hasBackorderStatus = item.statusId === B2BWaveProductStatuses.BACKORDER;
  return hasBackorderStatus || Boolean(item.canBackorder && item.inventory <= 0);
}
function hasBackorderedItems(items) {
  return items.some(isCartItemBackordered);
}
function hasMetrcTrackedItems(items) {
  return items.some((item) => item.requiresMetrcTracking !== false);
}
function getBackorderedItems(items) {
  return items.filter(isCartItemBackordered);
}

// src/cart/cart-storage.ts
var STATIC_SALT = "flote-cart-security-salt-v1";
var getCartStorageLogger = () => logger.child({ component: "CartStorage" });
var CartStorage = class {
  static storageKey = "";
  static encryptionKey = null;
  static saveTimeout = null;
  static isInitialized = false;
  static async init(instanceId, companyId, userId) {
    const log = getCartStorageLogger();
    try {
      if (!this.isCryptoAvailable()) {
        throw new Error("WebCrypto API is not available");
      }
      if (!this.isAvailable()) {
        throw new Error("localStorage is not available");
      }
      this.storageKey = `flote-cart-v1::${instanceId}::${companyId}::${userId}`;
      log.debug("Cart storage key generated", { storageKey: this.storageKey });
      const keyMaterial = userId + companyId + STATIC_SALT;
      const encoder = new TextEncoder();
      const keyData = await crypto.subtle.digest("SHA-256", encoder.encode(keyMaterial));
      const keyBuffer = keyData.slice(0, 16);
      this.encryptionKey = await crypto.subtle.importKey(
        "raw",
        keyBuffer,
        { name: "AES-GCM", length: 128 },
        false,
        ["encrypt", "decrypt"]
      );
      this.isInitialized = true;
      log.info("Cart storage initialized", { storageKey: this.storageKey });
    } catch (error) {
      this.isInitialized = false;
      this.encryptionKey = null;
      log.error("Cart storage initialization failed", { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }
  static async encrypt(data) {
    if (!this.encryptionKey) {
      throw new Error("Encryption key not initialized");
    }
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      this.encryptionKey,
      encoder.encode(data)
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  }
  static async decrypt(encryptedData) {
    if (!this.encryptionKey) {
      throw new Error("Encryption key not initialized");
    }
    const combined = new Uint8Array(
      atob(encryptedData).split("").map((char) => char.charCodeAt(0))
    );
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      this.encryptionKey,
      ciphertext
    );
    return new TextDecoder().decode(decrypted);
  }
  static async generateChecksum(data) {
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(data));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  static async save(cartState) {
    const log = getCartStorageLogger();
    if (!this.isInitialized || !this.encryptionKey) {
      log.debug("Cart storage not initialized, skipping save");
      return;
    }
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveTimeout = setTimeout(async () => {
      try {
        log.debug("Saving cart", {
          storageKey: this.storageKey,
          itemCount: cartState.items.length,
          securityContextCompanyId: cartState.securityContext?.companyId,
          securityContextUserId: cartState.securityContext?.userId
        });
        const persistedData = {
          version: 1,
          timestamp: Date.now(),
          data: {
            items: cartState.items,
            securityContext: cartState.securityContext,
            lastSyncedAt: cartState.lastSyncedAt,
            instanceId: cartState.instanceId
          }
        };
        const dataString = JSON.stringify(persistedData);
        const checksum = await this.generateChecksum(dataString);
        const envelope = { v: 1, ts: Date.now(), checksum, data: persistedData };
        const encryptedData = await this.encrypt(JSON.stringify(envelope));
        localStorage.setItem(this.storageKey, encryptedData);
        log.info("Cart saved successfully", {
          storageKey: this.storageKey,
          itemCount: cartState.items.length,
          dataLength: encryptedData.length
        });
      } catch (error) {
        log.error("Failed to save cart", { error: error instanceof Error ? error.message : String(error) });
      }
    }, DEFAULT_CART_CONFIG.storageDebounceMs);
  }
  static async load() {
    const log = getCartStorageLogger();
    if (!this.isInitialized || !this.encryptionKey) {
      log.debug("Cart storage not initialized, skipping load");
      return null;
    }
    try {
      log.debug("Attempting to load cart", { storageKey: this.storageKey });
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        log.debug("No cart data found in localStorage", { storageKey: this.storageKey });
        return null;
      }
      log.debug("Found raw cart data", { dataLength: raw.length });
      const decryptedData = await this.decrypt(raw);
      const envelope = JSON.parse(decryptedData);
      if (!envelope.v || !envelope.checksum || !envelope.data) {
        log.warn("Invalid cart envelope structure");
        this.clear();
        return null;
      }
      const dataString = JSON.stringify(envelope.data);
      const expectedChecksum = await this.generateChecksum(dataString);
      if (envelope.checksum !== expectedChecksum) {
        log.warn("Cart checksum mismatch - possible tampering");
        this.clear();
        return null;
      }
      const persistedData = envelope.data;
      const ageMs = Date.now() - persistedData.timestamp;
      if (ageMs > DEFAULT_CART_CONFIG.expirationMs) {
        log.info("Cart expired", { ageMs, maxAgeMs: DEFAULT_CART_CONFIG.expirationMs });
        this.clear();
        return null;
      }
      if (persistedData.version !== 1) {
        log.warn("Unsupported cart schema version", { version: persistedData.version });
        return null;
      }
      log.info("Cart loaded successfully", {
        itemCount: persistedData.data.items.length,
        securityContextCompanyId: persistedData.data.securityContext?.companyId,
        securityContextUserId: persistedData.data.securityContext?.userId
      });
      return persistedData.data;
    } catch (error) {
      log.error("Failed to load cart", { error: error instanceof Error ? error.message : String(error) });
      this.clear();
      return null;
    }
  }
  static clear() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
    }
  }
  static isAvailable() {
    try {
      const testKey = "__flote_storage_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
  static isCryptoAvailable() {
    return !!(window.crypto && window.crypto.subtle);
  }
  static getStorageKey() {
    return this.storageKey;
  }
  static resetEncryption() {
    this.encryptionKey = null;
    this.storageKey = "";
    this.isInitialized = false;
  }
};

// src/cart/cart-reducer.ts
import { getTotalInventory } from "@flote/utils";
function createInitialCartState() {
  return {
    items: [],
    isOpen: false,
    totalItems: 0,
    totalPrice: 0,
    securityContext: null,
    isLoading: false,
    loadingStates: {
      isLoading: false,
      isAddingItem: false,
      itemOperations: {},
      isClearingCart: false
    },
    lastSyncedAt: null,
    instanceId: generateInstanceId()
  };
}
function cartReducer(state, action) {
  switch (action.type) {
    case "ADD_ITEM": {
      const { product, quantity = 1, securityContext, brandName } = action.payload;
      const existingItemIndex = state.items.findIndex((item) => item.productId === product.id.toString());
      if (existingItemIndex !== -1) {
        const existingItem = state.items[existingItemIndex];
        const step = existingItem.orderIncrement ?? 1;
        const effectiveMax = Math.min(
          existingItem.maxOrderQuantity ?? DEFAULT_CART_CONFIG.maxQuantityPerItem,
          DEFAULT_CART_CONFIG.maxQuantityPerItem
        );
        const raw = Math.min(existingItem.quantity + quantity, effectiveMax);
        const newQuantity = step > 1 ? Math.round(raw / step) * step : raw;
        const updatedItems2 = [...state.items];
        updatedItems2[existingItemIndex] = { ...existingItem, quantity: newQuantity };
        const totals2 = calculateCartTotals(updatedItems2);
        return {
          ...state,
          items: updatedItems2,
          ...totals2,
          securityContext,
          lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      }
      if (state.items.length >= DEFAULT_CART_CONFIG.maxItems) {
        console.warn(`Cannot add item: cart limit of ${DEFAULT_CART_CONFIG.maxItems} items reached`);
        return state;
      }
      const newItem = createCartItem(product, quantity, securityContext, brandName);
      const minQty = newItem.minOrderQuantity ?? 1;
      if (newItem.quantity < minQty) {
        newItem.quantity = minQty;
      }
      const updatedItems = [...state.items, newItem];
      const totals = calculateCartTotals(updatedItems);
      return {
        ...state,
        items: updatedItems,
        ...totals,
        securityContext,
        lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
    }
    case "REMOVE_ITEM": {
      const updatedItems = state.items.filter((item) => item.id !== action.payload.id);
      const totals = calculateCartTotals(updatedItems);
      return { ...state, items: updatedItems, ...totals, lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString() };
    }
    case "UPDATE_QUANTITY": {
      const { id, quantity } = action.payload;
      const item = state.items.find((i) => i.id === id);
      const step = item?.orderIncrement ?? 1;
      const minQty = item?.minOrderQuantity ?? 1;
      if (quantity < minQty) {
        const updatedItems2 = state.items.filter((i) => i.id !== id);
        const totals2 = calculateCartTotals(updatedItems2);
        return { ...state, items: updatedItems2, ...totals2, lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString() };
      }
      const effectiveMax = Math.min(
        item?.maxOrderQuantity ?? DEFAULT_CART_CONFIG.maxQuantityPerItem,
        DEFAULT_CART_CONFIG.maxQuantityPerItem
      );
      const raw = Math.min(quantity, effectiveMax);
      const clampedQuantity = step > 1 ? Math.round(raw / step) * step || step : raw;
      const updatedItems = state.items.map(
        (i) => i.id === id ? { ...i, quantity: clampedQuantity } : i
      );
      const totals = calculateCartTotals(updatedItems);
      return { ...state, items: updatedItems, ...totals, lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString() };
    }
    case "CLEAR_CART":
      return { ...state, items: [], totalItems: 0, totalPrice: 0, lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString() };
    case "TOGGLE_CART":
      return { ...state, isOpen: !state.isOpen };
    case "SET_CART_OPEN":
      return { ...state, isOpen: action.payload };
    case "LOAD_CART": {
      const { items, securityContext, lastSyncedAt } = action.payload;
      const totals = calculateCartTotals(items);
      return { ...state, items, ...totals, securityContext, lastSyncedAt };
    }
    case "SET_SECURITY_CONTEXT":
      return { ...state, securityContext: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload, loadingStates: { ...state.loadingStates, isLoading: action.payload } };
    case "SET_ADDING_ITEM_LOADING":
      return { ...state, loadingStates: { ...state.loadingStates, isAddingItem: action.payload } };
    case "SET_ITEM_REMOVING_LOADING": {
      const { itemId, isLoading } = action.payload;
      const existingOp = state.loadingStates.itemOperations[itemId] ?? { isRemoving: false, isUpdating: false };
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          itemOperations: {
            ...state.loadingStates.itemOperations,
            [itemId]: { ...existingOp, isRemoving: isLoading }
          }
        }
      };
    }
    case "SET_ITEM_UPDATING_LOADING": {
      const { itemId, isLoading } = action.payload;
      const existingOp = state.loadingStates.itemOperations[itemId] ?? { isRemoving: false, isUpdating: false };
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          itemOperations: {
            ...state.loadingStates.itemOperations,
            [itemId]: { ...existingOp, isUpdating: isLoading }
          }
        }
      };
    }
    case "SET_CLEARING_CART_LOADING":
      return { ...state, loadingStates: { ...state.loadingStates, isClearingCart: action.payload } };
    case "SYNC_CART_ITEMS": {
      const { products } = action.payload;
      const productMap = new Map(products.map((p) => [p.id.toString(), p]));
      const updatedItems = state.items.map((item) => {
        const product = productMap.get(item.productId);
        if (!product) return item;
        const syncedPrice = product.pricing?.price ? Number.parseFloat(product.pricing.price) : product.priceMsrp;
        const syncedCustomerPrice = product.customerPrice?.price ? Number.parseFloat(product.customerPrice.price) : void 0;
        const syncedBuyerPrice = product.buyerPrice?.amount ? Number.parseFloat(product.buyerPrice.amount) : void 0;
        return {
          ...item,
          price: syncedPrice && Number.isFinite(syncedPrice) ? syncedPrice : item.price,
          customerPrice: syncedCustomerPrice && Number.isFinite(syncedCustomerPrice) ? syncedCustomerPrice : void 0,
          buyerPrice: syncedBuyerPrice && Number.isFinite(syncedBuyerPrice) ? syncedBuyerPrice : void 0,
          buyerPriceTiers: product.buyerPriceTiers,
          discountTiers: product.discountTiers,
          statusId: product.statusId ?? void 0,
          canBackorder: product.canBackorder ?? void 0,
          requiresMetrcTracking: product.requiresMetrcTracking ?? false,
          minOrderQuantity: product.minOrderQuantity ?? item.minOrderQuantity,
          maxOrderQuantity: product.maxOrderQuantity ?? item.maxOrderQuantity,
          orderIncrement: product.orderIncrement ?? item.orderIncrement,
          inventory: getTotalInventory(product)
        };
      });
      const totals = calculateCartTotals(updatedItems);
      return { ...state, items: updatedItems, ...totals, lastSyncedAt: (/* @__PURE__ */ new Date()).toISOString() };
    }
    default:
      return state;
  }
}

// src/cart/cart-security.ts
var CART_SECURITY_CONFIG = {
  MAX_CONTEXT_AGE_MS: 24 * 60 * 60 * 1e3,
  MIN_SESSION_ID_LENGTH: 16,
  COMPANY_ID_PATTERN: /^[a-zA-Z0-9_-]+$/,
  USER_ID_PATTERN: /^[a-zA-Z0-9_-]+$/,
  STORAGE_NAMESPACE: "flote-cart-v1"
};
function validateSecurityContext(context) {
  if (!context || typeof context !== "object") return false;
  const ctx = context;
  if (typeof ctx.companyId !== "string" || typeof ctx.userId !== "string" || typeof ctx.sessionId !== "string") {
    return false;
  }
  if (!CART_SECURITY_CONFIG.COMPANY_ID_PATTERN.test(ctx.companyId)) return false;
  if (!CART_SECURITY_CONFIG.USER_ID_PATTERN.test(ctx.userId)) return false;
  if (ctx.sessionId.length < CART_SECURITY_CONFIG.MIN_SESSION_ID_LENGTH) return false;
  return true;
}
function sanitizeSecurityContext(context) {
  if (!validateSecurityContext(context)) {
    throw new CartError("Invalid security context provided", "INVALID_SECURITY_CONTEXT", { context });
  }
  return {
    companyId: context.companyId.trim(),
    userId: context.userId.trim(),
    sessionId: context.sessionId.trim()
  };
}
function isSecurityContextEqual(context1, context2) {
  if (!context1 || !context2) return context1 === context2;
  return context1.companyId === context2.companyId && context1.userId === context2.userId;
}
function validateCartItemOwnership(item, securityContext) {
  return item.companyId === securityContext.companyId && item.addedBy === securityContext.userId;
}
function filterCartItemsByOwnership(items, securityContext) {
  return items.filter((item) => validateCartItemOwnership(item, securityContext));
}
function validateCartCompanyIsolation(cartState, expectedContext) {
  const errors = [];
  const validItems = [];
  if (!isSecurityContextEqual(cartState.securityContext, expectedContext)) {
    errors.push("Cart security context does not match expected context");
  }
  for (const item of cartState.items) {
    if (validateCartItemOwnership(item, expectedContext)) {
      validItems.push(item);
    } else {
      errors.push(`Item ${item.id} does not belong to company ${expectedContext.companyId}`);
    }
  }
  return { isValid: errors.length === 0, errors, validItems };
}
function generateSecureStorageKey(instanceId, suffix) {
  if (!instanceId || typeof instanceId !== "string" || instanceId.length < 8) {
    throw new CartError("Invalid instance ID for storage key generation", "INVALID_SECURITY_CONTEXT", { instanceId });
  }
  const baseKey = `${CART_SECURITY_CONFIG.STORAGE_NAMESPACE}::${instanceId}`;
  return suffix ? `${baseKey}::${suffix}` : baseKey;
}
function generateSecureSessionId() {
  const uuid = crypto.randomUUID();
  const timestamp = Date.now().toString(36);
  return `${uuid}-${timestamp}`;
}
function validateCartStateIntegrity(cartState) {
  if (!cartState || typeof cartState !== "object") return false;
  const state = cartState;
  const requiredFields = ["items", "isOpen", "totalItems", "totalPrice", "isLoading", "lastSyncedAt", "instanceId"];
  for (const field of requiredFields) {
    if (!(field in state)) return false;
  }
  if (!Array.isArray(state.items)) return false;
  for (const item of state.items) {
    if (!item || typeof item !== "object") return false;
    const cartItem = item;
    if (!cartItem.companyId || !cartItem.addedBy || !cartItem.productId) return false;
  }
  return true;
}
function logSecurityEvent(type, message, details) {
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    console.warn(`[Cart Security] ${type}: ${message}`, details);
  }
}
function createSecurityAudit(cartState, securityContext) {
  const issues = [];
  const warnings = [];
  const recommendations = [];
  if (!securityContext) {
    issues.push("No security context available");
    recommendations.push("Set security context before adding items to cart");
  } else {
    try {
      sanitizeSecurityContext(securityContext);
    } catch {
      issues.push("Invalid security context format");
      recommendations.push("Validate security context format before use");
    }
  }
  if (securityContext && cartState.items.length > 0) {
    const validation = validateCartCompanyIsolation(cartState, securityContext);
    if (!validation.isValid) {
      issues.push(...validation.errors);
      recommendations.push("Clear cart when company context changes");
    }
  }
  return {
    isSecure: issues.length === 0,
    issues,
    warnings,
    recommendations
  };
}

// src/cart/CartProvider.tsx
import React, { useReducer, useCallback, useMemo as useMemo2, useEffect, useRef } from "react";

// src/cart/auth-cart-bridge.ts
function createCartSecurityContext(customer) {
  const userId = String(customer.id);
  return {
    companyId: customer.companyNumber ? String(customer.companyNumber) : userId,
    userId,
    sessionId: generateSecureSessionId()
  };
}
function validateCustomerForCart(customer) {
  return !!(customer && (typeof customer.id === "number" || typeof customer.id === "string") && (customer.companyNumber || customer.id));
}
function isSameCompanyContext(customer1, customer2) {
  if (!validateCustomerForCart(customer1) || !validateCustomerForCart(customer2)) {
    return false;
  }
  const company1 = customer1.companyNumber || String(customer1.id);
  const company2 = customer2.companyNumber || String(customer2.id);
  return company1 === company2 && String(customer1.id) === String(customer2.id);
}

// src/cart/CartProvider.tsx
import { jsx } from "react/jsx-runtime";
var cartLogger = logger.child({ component: "CartProvider" });
var CartProvider = React.memo(({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, createInitialCartState());
  const initRef = useRef(false);
  const encryptionInitializedRef = useRef(false);
  const previousCustomerRef = useRef(null);
  const isExternalLoadRef = useRef(false);
  const { customer, isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { track } = useAnalyticsSafe();
  const currentSecurityContext = useMemo2(() => {
    if (!isAuthenticated || !validateCustomerForCart(customer)) {
      cartLogger.debug("No security context - not authenticated or invalid customer", {
        isAuthenticated,
        hasCustomer: !!customer,
        customerId: customer?.id,
        companyNumber: customer?.companyNumber
      });
      return null;
    }
    const ctx = createCartSecurityContext(customer);
    cartLogger.debug("Created security context", {
      companyId: ctx.companyId,
      userId: ctx.userId,
      customerId: customer.id,
      companyNumber: customer.companyNumber
    });
    return ctx;
  }, [customer, isAuthenticated]);
  useEffect(() => {
    const previousCustomer = previousCustomerRef.current;
    previousCustomerRef.current = customer;
    if (!initRef.current || authLoading) return;
    if (previousCustomer && !customer) {
      cartLogger.info("User logged out, resetting cart state");
      dispatch({ type: "CLEAR_CART" });
      CartStorage.resetEncryption();
      initRef.current = false;
      encryptionInitializedRef.current = false;
      return;
    }
    if (previousCustomer && customer && !isSameCompanyContext(previousCustomer, customer)) {
      cartLogger.info("Company context changed, clearing cart");
      dispatch({ type: "CLEAR_CART" });
      CartStorage.clear();
      CartStorage.resetEncryption();
      initRef.current = false;
      encryptionInitializedRef.current = false;
      return;
    }
    if (previousCustomer && customer && previousCustomer.companyNumber === customer.companyNumber && previousCustomer.id !== customer.id) {
      cartLogger.info("Different user logged in, clearing cart");
      dispatch({ type: "CLEAR_CART" });
      CartStorage.clear();
      CartStorage.resetEncryption();
      initRef.current = false;
      encryptionInitializedRef.current = false;
    }
  }, [customer, authLoading]);
  useEffect(() => {
    if (!currentSecurityContext) return;
    const handleStorageChange = async (event) => {
      if (event.key !== CartStorage.getStorageKey()) return;
      try {
        if (event.newValue) {
          const updatedState = await CartStorage.load();
          if (updatedState) {
            const validation = validateCartCompanyIsolation(updatedState, currentSecurityContext);
            if (validation.isValid) {
              isExternalLoadRef.current = true;
              dispatch({
                type: "LOAD_CART",
                payload: {
                  items: updatedState.items,
                  securityContext: currentSecurityContext,
                  lastSyncedAt: updatedState.lastSyncedAt
                }
              });
            }
          }
        } else {
          isExternalLoadRef.current = true;
          dispatch({ type: "CLEAR_CART" });
        }
      } catch (error) {
        cartLogger.warn("Failed to sync cart from other tab", {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [currentSecurityContext]);
  useEffect(() => {
    if (!currentSecurityContext && encryptionInitializedRef.current) {
      initRef.current = false;
      encryptionInitializedRef.current = false;
    }
    if (initRef.current || !currentSecurityContext) return;
    const initializeCart = async () => {
      try {
        cartLogger.info("Initializing cart storage", {
          instanceId: state.instanceId,
          companyId: currentSecurityContext.companyId,
          userId: currentSecurityContext.userId
        });
        if (!CartStorage.isCryptoAvailable()) {
          cartLogger.warn("WebCrypto API not available - cart persistence disabled");
          return;
        }
        await CartStorage.init(
          state.instanceId,
          currentSecurityContext.companyId,
          currentSecurityContext.userId
        );
        initRef.current = true;
        encryptionInitializedRef.current = true;
        const persistedState = await CartStorage.load();
        cartLogger.debug("Loaded persisted cart state", {
          hasPersistedState: !!persistedState,
          itemCount: persistedState?.items?.length ?? 0,
          persistedCompanyId: persistedState?.securityContext?.companyId,
          persistedUserId: persistedState?.securityContext?.userId,
          currentCompanyId: currentSecurityContext.companyId,
          currentUserId: currentSecurityContext.userId
        });
        if (persistedState) {
          const validation = validateCartCompanyIsolation(persistedState, currentSecurityContext);
          cartLogger.debug("Company isolation validation", {
            isValid: validation.isValid,
            errorCount: validation.errors.length,
            validItemCount: validation.validItems.length,
            firstError: validation.errors[0] || null
          });
          if (!validation.isValid) {
            cartLogger.info("Loaded cart contains items from different company, using valid items only");
            if (validation.validItems.length > 0) {
              dispatch({
                type: "LOAD_CART",
                payload: {
                  items: validation.validItems,
                  securityContext: currentSecurityContext,
                  lastSyncedAt: persistedState.lastSyncedAt
                }
              });
            } else {
              dispatch({ type: "CLEAR_CART" });
            }
            return;
          }
          const audit = createSecurityAudit(persistedState, currentSecurityContext);
          cartLogger.debug("Security audit result", {
            isSecure: audit.isSecure,
            issueCount: audit.issues.length,
            warningCount: audit.warnings.length,
            firstIssue: audit.issues[0] || null
          });
          if (audit.warnings.length > 0) {
            cartLogger.info("Security audit warnings", { warnings: audit.warnings.join(", ") });
          }
          if (!audit.isSecure) {
            cartLogger.info("Loaded cart failed security audit, clearing cart", { issues: audit.issues.join(", ") });
            dispatch({ type: "CLEAR_CART" });
            return;
          }
          cartLogger.info("Cart loaded successfully", { itemCount: persistedState.items.length });
          dispatch({
            type: "LOAD_CART",
            payload: {
              items: persistedState.items,
              securityContext: currentSecurityContext,
              lastSyncedAt: persistedState.lastSyncedAt
            }
          });
        } else {
          cartLogger.debug("No persisted cart found");
        }
      } catch (error) {
        cartLogger.error("Failed to initialize cart storage", {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    };
    initializeCart();
  }, [state.instanceId, currentSecurityContext]);
  useEffect(() => {
    if (!initRef.current || !currentSecurityContext || !encryptionInitializedRef.current) return;
    if (isExternalLoadRef.current) {
      isExternalLoadRef.current = false;
      return;
    }
    const saveCart = async () => {
      try {
        const audit = createSecurityAudit(state, currentSecurityContext);
        if (!audit.isSecure) {
          cartLogger.warn("Attempted to save cart with security issues", { issues: audit.issues.join(", ") });
          return;
        }
        await CartStorage.save(state);
      } catch (error) {
        cartLogger.error("Failed to save cart", {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    };
    saveCart();
  }, [state.items, state.securityContext, currentSecurityContext]);
  const addItem = useCallback((input) => {
    if (!isAuthenticated || !currentSecurityContext) {
      cartLogger.info("Attempted to add item without authentication");
      return;
    }
    dispatch({
      type: "ADD_ITEM",
      payload: {
        product: input.product,
        quantity: input.quantity || 1,
        securityContext: currentSecurityContext,
        brandName: input.brandName
      }
    });
    track(AnalyticsEvents.PRODUCT_ADDED_TO_CART, {
      productId: String(input.product.id),
      productName: input.product.name,
      quantity: input.quantity || 1,
      price: input.product.customerPrice?.price ? parseFloat(input.product.customerPrice.price) : input.product.price1 ?? 0,
      brandName: input.brandName
    });
  }, [isAuthenticated, currentSecurityContext, track]);
  const removeItem = useCallback((id) => {
    if (!isAuthenticated || !currentSecurityContext) return;
    const item = state.items.find((i) => i.id === id);
    if (item) {
      track(AnalyticsEvents.PRODUCT_REMOVED_FROM_CART, {
        productId: String(item.productId),
        productName: item.name,
        quantity: item.quantity
      });
    }
    dispatch({ type: "REMOVE_ITEM", payload: { id } });
  }, [isAuthenticated, currentSecurityContext, state.items, track]);
  const updateQuantity = useCallback((id, quantity) => {
    if (!isAuthenticated || !currentSecurityContext) return;
    dispatch({ type: "UPDATE_QUANTITY", payload: { id, quantity } });
  }, [isAuthenticated, currentSecurityContext]);
  const clearCart = useCallback(() => {
    if (state.items.length > 0) {
      track(AnalyticsEvents.CART_CLEARED, {});
    }
    dispatch({ type: "CLEAR_CART" });
  }, [state.items.length, track]);
  const toggleCart = useCallback(() => {
    dispatch({ type: "TOGGLE_CART" });
  }, []);
  const setCartOpen = useCallback((isOpen) => {
    dispatch({ type: "SET_CART_OPEN", payload: isOpen });
  }, []);
  const syncCartItems = useCallback((products) => {
    if (state.items.length === 0) return;
    dispatch({ type: "SYNC_CART_ITEMS", payload: { products } });
  }, [state.items.length]);
  const contextValue = useMemo2(() => ({
    items: state.items,
    isOpen: state.isOpen,
    totalItems: state.totalItems,
    totalPrice: state.totalPrice,
    isLoading: state.isLoading || authLoading,
    loadingStates: state.loadingStates,
    lastSyncedAt: state.lastSyncedAt,
    securityContext: currentSecurityContext,
    isAuthenticated,
    authLoading,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    setCartOpen,
    syncCartItems
  }), [
    state.items,
    state.isOpen,
    state.totalItems,
    state.totalPrice,
    state.isLoading,
    state.loadingStates,
    state.lastSyncedAt,
    currentSecurityContext,
    isAuthenticated,
    authLoading,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    setCartOpen,
    syncCartItems
  ]);
  return /* @__PURE__ */ jsx(CartContext.Provider, { value: contextValue, children });
});
CartProvider.displayName = "CartProvider";

export {
  CartError,
  DEFAULT_CART_CONFIG,
  CartContext,
  useCart,
  useCartDrawer,
  useProductInCart,
  useCartTotals,
  useCartLoading,
  useCartAuth,
  getEffectivePrice,
  getPriceDisplayInfo,
  formatCurrency,
  isCartItemBackordered,
  hasBackorderedItems,
  hasMetrcTrackedItems,
  getBackorderedItems,
  CartStorage,
  createInitialCartState,
  cartReducer,
  CART_SECURITY_CONFIG,
  validateSecurityContext,
  sanitizeSecurityContext,
  isSecurityContextEqual,
  validateCartItemOwnership,
  filterCartItemsByOwnership,
  validateCartCompanyIsolation,
  generateSecureStorageKey,
  generateSecureSessionId,
  validateCartStateIntegrity,
  logSecurityEvent,
  createSecurityAudit,
  createCartSecurityContext,
  validateCustomerForCart,
  isSameCompanyContext,
  CartProvider
};
